import { DashboardWidget } from '@treasury/domain/dashboard/dashboard-widget.entity';
import { TmBaseComponent } from '@treasury/presentation';
import {
    circleCheckmarkFilledIcon,
    closeIcon,
    plusIcon,
} from '@treasury/presentation/assets/icons';
import '@treasury/presentation/components/tm-button';
import '@treasury/presentation/components/tm-context-menu';
import '@treasury/presentation/components/tm-icon';
import { ContextMenuItem } from '@vaadin/context-menu';
import { html } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';

export const tagName = 'add-widget-menu';

@customElement(tagName)
export class AddWidgetMenu extends TmBaseComponent {
    @property({ type: Array })
    public availableWidgets: DashboardWidget[] = [];

    @state()
    private selectedWidgets: DashboardWidget[] = [];

    @state()
    private items: Array<ContextMenuItem> = [];

    @state()
    private closeMenu = false;

    protected updated(changedProperties: Map<string | number | symbol, unknown>) {
        if (changedProperties.has('availableWidgets')) {
            this.items =
                this.availableWidgets.length > 0
                    ? this.availableWidgets.map(widget => {
                          return {
                              component: this.createWidgetMenuItem(widget),
                              click: () => {
                                  this.selectedWidgets.push(widget);
                                  this.selectedWidgets = [...this.selectedWidgets];
                              },
                          };
                      })
                    : this.createEmptyState();
            this.items = [
                { component: this.createListHeader(), disabled: true },
                { component: 'hr' },
                ...this.items,
                { component: this.createListFooter() },
            ];
        }
    }

    protected render() {
        return html`<tm-context-menu
            .openOn=${'click'}
            .closeOn=${'addWidgets'}
            .items=${this.items}
        >
            <tm-button importance="tertiary"
                ><tm-icon .icon=${plusIcon} color="#FFFFFF"></tm-icon></tm-button
        ></tm-context-menu>`;
    }

    private createListHeader() {
        const header = document.createElement('div');
        const titleEl = document.createElement('span');
        const closeEl = document.createElement('tm-icon');
        closeEl.icon = closeIcon;
        closeEl.color = '#70757A';
        closeEl.addEventListener('click', () => {
            this.selectedWidgets = [];
            closeEl.dispatchEvent(
                new CustomEvent('addWidgets', {
                    detail: this.selectedWidgets,
                    bubbles: true,
                    composed: true,
                })
            );
        });
        titleEl.innerHTML = 'Available Widgets';
        header.classList.add(
            'text-secondary',
            'text-lg',
            'pb-3',
            'flex',
            'items-center',
            'justify-between'
        );
        header.appendChild(titleEl);
        header.appendChild(closeEl);
        return header;
    }

    private createEmptyState() {
        const emptyState = document.createElement('div');
        const titleEl = document.createElement('span');
        titleEl.innerHTML = 'All available widgets already in use';
        emptyState.classList.add('text-secondary', 'text-md', 'py-3', 'w-full', 'text-center');
        emptyState.appendChild(titleEl);
        return [{ component: emptyState, disabled: true }];
    }

    private createWidgetMenuItem(widget: DashboardWidget) {
        const item = document.createElement('div');
        const wrapper = document.createElement('div');
        const icon = document.createElement('tm-icon');
        icon.icon = plusIcon;
        icon.color = '#788087';
        const descriptionEl = document.createElement('span');
        const description = widget.description ? `(${widget.description})` : '';
        const textEl = document.createElement('div');

        descriptionEl.innerHTML = description;
        descriptionEl.classList.add('text-xs', 'text-secondary');
        wrapper.classList.add(
            'py-3',
            'px-3',
            'mt-2',
            'rounded-lg',
            'cursor-pointer',
            'border',
            'flex',
            'align-center',
            'items-center',
            'justify-between'
        );

        textEl.classList.add('flex', 'flex-col');

        item.appendChild(wrapper);
        wrapper.addEventListener('click', () => {
            const index = this.selectedWidgets.indexOf(widget);
            if (index > -1) {
                this.selectedWidgets.splice(index, 1);
                icon.icon = plusIcon;
                icon.color = '#788087';
                wrapper.classList.remove('border-[var(--content-positive)]');
            } else {
                this.selectedWidgets.push(widget);
                icon.icon = circleCheckmarkFilledIcon;
                icon.color = '#3B6F2D';
                wrapper.classList.add('border-[var(--content-positive)]');
            }
            this.selectedWidgets = [...this.selectedWidgets];
        });
        textEl.appendChild(document.createTextNode(widget.title || ''));
        textEl.appendChild(descriptionEl);
        wrapper.appendChild(textEl);
        wrapper.appendChild(icon);
        return item;
    }

    private createListFooter() {
        const footer = document.createElement('div');
        footer.classList.add('flex', 'justify-center', 'py-3');
        const button = document.createElement('tm-button');
        button.importance = 'primary';
        button.innerHTML = 'Add Widgets';
        // add click event to button that dispatches an event with the selectedWidgets array
        button.addEventListener('click', () => {
            button.dispatchEvent(
                new CustomEvent('addWidgets', {
                    detail: this.selectedWidgets,
                    bubbles: true,
                    composed: true,
                })
            );
            this.dispatchEvent(
                new CustomEvent('addWidgets', {
                    detail: this.selectedWidgets,
                    bubbles: true,
                    composed: true,
                })
            );
            this.selectedWidgets = [];
        });
        footer.appendChild(button);
        return footer;
    }
}

declare global {
    interface HTMLElementTagNameMap {
        [tagName]: AddWidgetMenu;
    }
}
